import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle, CheckCircle2, Upload, X, FileText, Image, Info, Mail, AlertCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isValidEmailFormat, suggestEmailCorrection } from "../utils/emailValidation";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RED_FLAGS = [
  { key: 'dificultad_respirar', label: 'Dificultad para respirar' },
  { key: 'hinchazon_cara', label: 'Hinchazón de cara, labios o lengua' },
  { key: 'dolor_pecho', label: 'Dolor en el pecho' },
  { key: 'perdida_conciencia', label: 'Pérdida de conocimiento o desmayo' },
  { key: 'convulsion', label: 'Convulsión' },
  { key: 'debilidad_subita', label: 'Debilidad súbita de un brazo o pierna' },
  { key: 'confusion', label: 'Confusión o alteración del estado de conciencia' },
  { key: 'sangrado_vomitos', label: 'Sangrado importante o vómitos con sangre' },
  { key: 'erupcion_fiebre', label: 'Erupción cutánea extensa con fiebre o ampollas' },
  { key: 'dolor_abdominal', label: 'Dolor abdominal intenso y persistente' },
  { key: 'perdida_vision', label: 'Pérdida repentina de visión' }
];

// Tooltips explicativos para pacientes sobre cada tipo de consulta
const PATIENT_CATEGORY_TOOLTIPS = {
  resultados: "Utiliza esta opción si deseas consultar sobre resultados de estudios o análisis previamente solicitados por tu médico.",
  seguimiento: "Consultas relacionadas con el control de síntomas o seguimiento de una condición ya evaluada.",
  medicacion: "Efectos adversos, ajuste de dosis, dudas sobre tu medicación.",
  recetas: "Renovación de medicación que ya tomás, sin cambios.",
  documentacion: "Solicitudes administrativas: resumen de historia clínica, certificados médicos, informes, órdenes, formularios para prepaga, autorizaciones o documentación relacionada con tu atención.",
  otra: "Selecciona esta opción si tu consulta no encaja claramente en las categorías anteriores."
};

// Cards apiladas verticalmente para el step 2. Orden estratégico:
// 'recetas' primero (canal de mayor volumen esperado en Fleni).
// Labels intencionalmente cortos para mobile; la explicación va en tooltip.
const CATEGORY_CARDS = [
  { id: "recetas",       label: "Recetas" },
  { id: "resultados",    label: "Interpretación de resultados" },
  { id: "seguimiento",   label: "Seguimiento / control" },
  { id: "medicacion",    label: "Consulta sobre tu tratamiento" },
  { id: "documentacion", label: "Documentación" },
  { id: "otra",          label: "Otra consulta" },
];

const IntakeForm = ({ doctor = null, institution: propInstitution = null, source = null, sourceSlug = null, onCancel = null }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!propInstitution);
  const [submitting, setSubmitting] = useState(false);
  const [institution, setInstitution] = useState(propInstitution);
  const [step, setStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Past red-flags reported by the patient (symptoms they had but are no longer active).
  // Each item: { key, label }. These do NOT block submission but mark the consult as
  // "high_clinical_priority" so the doctor sees a warning badge.
  const [pastRedFlags, setPastRedFlags] = useState([]);

  // While user is confirming whether a red-flag is current or past, we hold it here.
  // null = no dialog open. { key, label } = dialog open for that symptom.
  const [redFlagPendingConfirm, setRedFlagPendingConfirm] = useState(null);

  // True when the form has been pre-filled from a returning patient's authenticated session.
  // Used to show a small "welcome back" banner so the user knows to verify their data.
  const [prefilledFromAuth, setPrefilledFromAuth] = useState(false);

  // When the assigned doctor has configured an "ausencia temporal" SLA (>= 720h),
  // we MUST inform the patient BEFORE they fill the form so they can opt out.
  // Acknowledged is persisted only for the current navigation (not localStorage),
  // so re-entering the link from another session shows the warning again.
  const [showAbsenceWarning, setShowAbsenceWarning] = useState(false);
  const [absenceAcknowledged, setAbsenceAcknowledged] = useState(false);
  const ABSENCE_THRESHOLD_HOURS = 360; // > 15 days is considered "extended absence"
  
  const [formData, setFormData] = useState({
    // Who is filling out the form
    is_third_party: false,
    submitter_name: "",
    submitter_email: "",
    submitter_dni: "",
    submitter_relationship: "",
    submitter_relationship_other: "",
    patient_authorization_declared: false,

    patient_name: "",
    patient_email: "",
    patient_email_confirm: "",
    patient_dni: "",
    patient_phone: "",
    obra_social: "",
    credencial_number: "",
    category: "",
    consult_reason: "",
    red_flags: {
      dificultad_respirar: false,
      hinchazon_cara: false,
      dolor_pecho: false,
      perdida_conciencia: false,
      convulsion: false,
      debilidad_subita: false,
      confusion: false,
      sangrado_vomitos: false,
      erupcion_fiebre: false,
      dolor_abdominal: false,
      perdida_vision: false
    },
    accepted_terms: false,
    specific_fields: {
      attachments: [],
      results_comment: "",
      followup_description: "",
      current_medication: "",
      medication_dose: "",
      medication_since: "",
      medication_concern: "",
      other_medications: "",
      document_type: "",
      document_reason: "",
      other_description: "",
      // Receta requests (free-text list of meds to renew, no clinical questions)
      medications_text: "",
      // Campos de triaje para efectos adversos
      medication_subtipo: "", // "consulta_general" | "efecto_adverso"
      inicio_sintomas: "", // "menos_24h" | "1_2_dias" | "mas_2_dias"
      severidad: "", // "leve" | "moderado" | "intenso"
      adverse_red_flags: [] // array de strings
    }
  });

  // State for "Repetir solicitud anterior" picker (last 5 recetas)
  const [previousRecetas, setPreviousRecetas] = useState([]);
  const [showPreviousRecetasModal, setShowPreviousRecetasModal] = useState(false);

  // Confirmation modal for recetas: patient reviews the list of meds before submitting
  const [showRecetaConfirm, setShowRecetaConfirm] = useState(false);

  // Load default institution on mount (only if not provided via props)
  const loadInstitution = useCallback(async () => {
    if (propInstitution) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/intake/default-institution`);
      if (response.ok) {
        const data = await response.json();
        setInstitution(data);
      }
    } catch (error) {
      console.error('Error loading institution:', error);
    } finally {
      setLoading(false);
    }
  }, [propInstitution]);

  useEffect(() => {
    loadInstitution();
  }, [loadInstitution]);

  // Detect "ausencia temporal" SLA from the doctor profile and surface a
  // blocking warning BEFORE the patient invests time filling the form / paying.
  useEffect(() => {
    if (!doctor) return;
    const customSla = doctor.custom_sla_hours;
    if (typeof customSla === 'number' && customSla >= ABSENCE_THRESHOLD_HOURS && !absenceAcknowledged) {
      setShowAbsenceWarning(true);
    }
  }, [doctor, absenceAcknowledged]);

  // Prefill from authenticated returning-patient session (?prefill_token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefillToken = params.get('prefill_token');
    if (!prefillToken) return;

    let cancelled = false;
    const fetchPrefill = async () => {
      try {
        const response = await fetch(`${API_URL}/api/intake/prefill?token=${encodeURIComponent(prefillToken)}`);
        if (!response.ok || cancelled) return;
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          patient_name: data.patient_name || prev.patient_name,
          patient_dni: data.patient_dni || prev.patient_dni,
          patient_email: data.patient_email || prev.patient_email,
          patient_email_confirm: data.patient_email || prev.patient_email_confirm,
          patient_phone: data.patient_phone || prev.patient_phone,
          obra_social: data.obra_social || prev.obra_social,
          credencial_number: data.credencial_number || prev.credencial_number,
        }));
        setPrefilledFromAuth(true);
      } catch (err) {
        // Silent fail: user just sees an empty form
      }
    };
    fetchPrefill();
    return () => { cancelled = true; };
  }, []);

  // SAFETY NET: if a CURRENT (active) red flag is checked (e.g., after navigation back),
  // immediately redirect to the urgency screen. Past red flags do NOT trigger this.
  // Recetas (prescription renewal) bypass red-flag triage by design.
  useEffect(() => {
    if (formData.category === 'recetas') return; // recetas skip clinical triage
    const activeFlag = RED_FLAGS.find(f => formData.red_flags[f.key]);
    if (activeFlag) {
      navigate('/urgencia', {
        state: {
          trigger: 'red_flag_intake',
          red_flag_key: activeFlag.key,
          red_flag_label: activeFlag.label,
          institution_slug: sourceSlug ? sourceSlug.split('/')[0] : null
        }
      });
    }
  }, [formData.red_flags, formData.category]);

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (uploadedFiles.length + files.length > 5) {
      toast.error("Máximo 5 archivos permitidos");
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Archivo demasiado grande (máx. 10MB)`);
        continue;
      }
      
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`${file.name}: Tipo de archivo no permitido`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const slug = sourceSlug ? sourceSlug.split('/')[0] : null;
        const uploadUrl = slug
          ? `${API_URL}/api/attachments/upload?institution_slug=${encodeURIComponent(slug)}`
          : `${API_URL}/api/attachments/upload`;
        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formDataUpload
        });
        
        if (response.ok) {
          const data = await response.json();
          setUploadedFiles(prev => [...prev, {
            id: data.attachment_id,
            name: data.original_filename,
            url: data.file_url,
            type: data.file_type,
            size: data.size
          }]);
          toast.success(`${file.name} subido correctamente`);
        } else {
          const error = await response.json();
          toast.error(`${file.name}: ${error.detail || 'Error al subir'}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`${file.name}: Error al subir`);
      }
    }
    
    setUploading(false);
    e.target.value = '';
  };
  
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Legal consent gate. Depends on the tenant's legal_config.terms_mode:
    //   - "explicit": patient must tick the checkbox (default post-legal-review).
    //   - "implicit": clicking "Enviar consulta" is itself the act of consent.
    //   - "none": no gate.
    const termsMode = institution?.legal_config?.terms_mode || 'explicit';
    if (termsMode === 'explicit' && !formData.accepted_terms) {
      toast.error("Debes aceptar los términos y condiciones");
      return;
    }

    // For recetas: intercept the submit and show a confirmation modal listing
    // every medication the patient is about to request. Prevents accidental submits
    // with an incomplete list (patient forgets a med → would have to open a new consult).
    if (formData.category === 'recetas' && !showRecetaConfirm) {
      const meds = (formData.specific_fields.medications_text || '').trim();
      if (!meds) {
        toast.error("Ingresá al menos un medicamento");
        return;
      }
      setShowRecetaConfirm(true);
      return;
    }

    setSubmitting(true);
    
    try {
      // Build structured reason based on category
      let structuredReason = formData.consult_reason;
      
      // Build triage metadata for medication adverse effects
      let triageData = null;
      
      if (formData.category === 'resultados') {
        structuredReason = `INTERPRETACIÓN DE RESULTADOS\n\n${formData.specific_fields.results_comment || formData.consult_reason}`;
      } else if (formData.category === 'seguimiento') {
        structuredReason = `SEGUIMIENTO / CONTROL\n\n${formData.specific_fields.followup_description || formData.consult_reason}`;
      } else if (formData.category === 'medicacion') {
        structuredReason = `CONSULTA SOBRE TU TRATAMIENTO\n\n${formData.specific_fields.medication_concern || formData.consult_reason}`;
        
        // Include triage data if it's an adverse effect consultation
        if (formData.specific_fields.medication_subtipo === 'efecto_adverso') {
          triageData = {
            subtipo: formData.specific_fields.medication_subtipo,
            inicio_sintomas: formData.specific_fields.inicio_sintomas,
            severidad: formData.specific_fields.severidad,
            red_flags: formData.specific_fields.adverse_red_flags
          };
        }
      } else if (formData.category === 'recetas') {
        const meds = (formData.specific_fields.medications_text || '').trim();
        structuredReason = `RECETAS (RENOVACIÓN SIN CAMBIOS)\n\n${meds}`;
      } else if (formData.category === 'documentacion') {
        structuredReason = `DOCUMENTACIÓN (CERTIFICADO / ORDEN / INFORME)\n\n${formData.specific_fields.document_reason || formData.consult_reason}`;
      } else if (formData.category === 'otra') {
        structuredReason = `NO ESTOY SEGURO / OTRA CONSULTA\n\n${formData.specific_fields.other_description || formData.consult_reason}`;
      }

      const response = await fetch(`${API_URL}/api/consults/submit-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_name: formData.patient_name,
          patient_email: formData.patient_email,
          patient_dni: formData.patient_dni,
          patient_phone: formData.patient_phone || null,
          obra_social: formData.obra_social || null,
          credencial_number: formData.credencial_number || null,
          category: formData.category,
          consult_reason: structuredReason,
          red_flags: formData.red_flags,
          past_red_flags: pastRedFlags,
          high_clinical_priority: pastRedFlags.length > 0,
          is_third_party: formData.is_third_party,
          submitter_name: formData.is_third_party ? formData.submitter_name : null,
          submitter_email: formData.is_third_party ? formData.submitter_email : null,
          submitter_dni: formData.is_third_party ? formData.submitter_dni : null,
          submitter_relationship: formData.is_third_party ? formData.submitter_relationship : null,
          submitter_relationship_other: formData.is_third_party && formData.submitter_relationship === "other" ? formData.submitter_relationship_other : null,
          patient_authorization_declared: formData.is_third_party && formData.patient_authorization_declared,
          patient_authorization_text: formData.is_third_party && formData.patient_authorization_declared
            ? "Declaro bajo juramento que el paciente me autorizó expresamente a realizar esta consulta médica en su nombre y a recibir información sobre su salud. Asumo la responsabilidad legal de esta declaración. (Ley 25.326 / Ley 26.529)"
            : null,
          triage_data: triageData,
          institution_id: institution?.id || null,
          // Smart link fields
          assigned_doctor_id: doctor?.id || null,
          source: source || "landing",
          source_slug: sourceSlug || null,
          // Receta-only field (free-text list of meds to renew)
          medications_text: formData.category === 'recetas' ? (formData.specific_fields.medications_text || null) : null,
          // Consent snapshot. Backend enriches this with ip_hash + accepted_at.
          consent: (() => {
            const cfg = institution?.legal_config || {};
            const mode = cfg.terms_mode || 'explicit';
            const text = mode === 'explicit'
              ? 'Acepto los terminos y condiciones y autorizo el tratamiento de mis datos personales para fines medicos.'
              : (cfg.implicit_consent_text || '');
            return {
              mode,
              text_snapshot: text,
              form_version: cfg.form_version || 'pilot-2026-02',
              user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 512),
            };
          })(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'rejected' || data.status === 'urgent_blocked') {
          // Navigate to urgency page for blocked consultations
          if (data.status === 'urgent_blocked') {
            navigate('/urgencia', { 
              state: { 
                message: data.message,
                consult_id: data.consult_id,
                triage_level: data.triage_level
              }
            });
          } else {
            toast.error(data.message);
            setTimeout(() => navigate('/'), 3000);
          }
        } else {
          // Link uploaded files to the consult
          if (uploadedFiles.length > 0) {
            try {
              await fetch(`${API_URL}/api/attachments/link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  consult_id: data.consult_id,
                  attachment_ids: uploadedFiles.map(f => f.id)
                })
              });
            } catch (linkError) {
              console.error('Error linking attachments:', linkError);
            }
          }
          
          toast.success(formData.category === 'recetas'
            ? "Solicitud de receta enviada"
            : "Consulta creada correctamente");
          // Persist reuse artifacts for the "Iniciar otra solicitud" CTA on the confirmation screen.
          try {
            if (data.reuse_prefill_token) {
              sessionStorage.setItem('fleni:reuse_prefill_token', data.reuse_prefill_token);
            }
            if (data.source_slug) {
              sessionStorage.setItem('fleni:last_source_slug', data.source_slug);
            }
          } catch (e) { /* sessionStorage unavailable — silent */ }
          setTimeout(() => {
            // Receta requests on tenants where Thales does not bill skip the payment screen.
            if (data.status === 'submitted_no_payment') {
              navigate(`/receta-confirmada/${data.consult_id}`);
            } else {
              navigate(`/payment/${data.consult_id}`);
            }
          }, 1000);
        }
      } else {
        toast.error(data.detail || "Error al enviar la consulta");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al conectar con el servidor");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Dynamic branding from institution
  const branding = institution?.branding || {};
  const primaryColor = branding.primary_color || "#1d366b";
  const logoUrl = branding.logo_url;  // null means show text instead of logo
  const institutionLogoAlt = institution?.name || "Fleni";

  // Build a human-readable label for the extended SLA (15 días a 1 mes, etc.)
  const formatAbsenceWindow = (hours) => {
    if (!hours) return "más tiempo del habitual";
    const days = Math.round(hours / 24);
    if (days >= 25) return "entre 15 días y 1 mes";
    if (days >= 15) return `aproximadamente ${days} días`;
    return `aproximadamente ${days} días`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Absence-warning blocking modal - shown BEFORE the patient invests time */}
      {showAbsenceWarning && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="absence-warning-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 border border-amber-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-11 w-11 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Tu profesional tiene una ausencia temporal
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Antes de continuar, queremos que sepas esto.
                </p>
              </div>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              {doctor?.name && (
                <p>
                  <strong>{doctor.name}</strong> configuró un período de ausencia temporal en su agenda.
                </p>
              )}
              <p>
                Por eso, tu consulta podría tardar <strong>{formatAbsenceWindow(doctor?.custom_sla_hours)}</strong> en ser respondida (más que el tiempo habitual).
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-900">
                  <strong>Sin cargo hasta la respuesta.</strong> No te cobramos nada hasta que el médico envíe la respuesta final a tu consulta.
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2.5">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">
                  <strong>Si tu consulta es urgente</strong>, no continúes con este flujo. Acudí a una guardia o emergencias.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  if (onCancel) onCancel();
                  else navigate('/');
                }}
                data-testid="absence-warning-cancel-btn"
              >
                Mejor no, volver
              </Button>
              <Button
                onClick={() => {
                  setAbsenceAcknowledged(true);
                  setShowAbsenceWarning(false);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="absence-warning-continue-btn"
              >
                Entiendo y quiero continuar
              </Button>
            </div>
          </div>
        </div>
      )}

      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              {logoUrl ? (
                <img src={logoUrl} alt={institutionLogoAlt} className="h-10 sm:h-12 object-contain" />
              ) : (
                <span className="text-lg font-bold" style={{ color: primaryColor }}>{institutionLogoAlt}</span>
              )}
            </div>
            {institution && (
              <div className="text-sm text-slate-600">
                {institution.name}
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Doctor header for smart links */}
        {doctor && (
          <div className="mb-8 rounded-xl border p-6" style={{ background: `linear-gradient(135deg, ${primaryColor}08, ${primaryColor}03)`, borderColor: `${primaryColor}20` }}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                <span className="text-white text-xl font-bold">
                  {doctor.public_name?.charAt(0) || 'M'}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Consultando con</p>
                <h2 className="text-lg font-bold text-slate-900">{doctor.public_name}</h2>
                {doctor.specialty && (
                  <p className="text-sm" style={{ color: primaryColor }}>{doctor.specialty}</p>
                )}
                {institution?.is_private_practice && doctor.consultation_price > 0 && (
                  <p className="text-sm text-slate-600 mt-1" data-testid="private-intake-price">
                    Consulta privada: <strong style={{ color: primaryColor }}>${doctor.consultation_price.toLocaleString('es-AR')}</strong>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Nueva Consulta</h1>
        </div>

        {/* Progress Steps - dynamic color */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'text-white' : 'bg-slate-200 text-slate-500'
                }`}
                style={step >= s ? { backgroundColor: primaryColor } : {}}
              >
                {s}
              </div>
              {s < 3 && (
                <div className="w-16 h-1 mx-2" style={{ backgroundColor: step > s ? primaryColor : '#e2e8f0' }} />
              )}
            </div>
          ))}
        </div>

        {/* Tenant context indicator — helps the patient (and the doctor if they're
            testing) confirm that they're on the correct bandeja. Fixes the confusion
            we had where consults appeared to go to the wrong inbox. */}
        {institution?.name && (
          <div
            className="mb-4 flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-500"
            data-testid="intake-tenant-indicator"
          >
            <span>Estás consultando en</span>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${institution.branding?.primary_color || '#1d366b'}15`,
                color: institution.branding?.primary_color || '#1d366b',
              }}
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: institution.branding?.primary_color || '#1d366b' }} />
              {institution.name}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            
            {/* Step 1: Patient Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900">Confirmá tus datos</h2>
                </div>

                {prefilledFromAuth && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2.5" data-testid="prefill-welcome-banner">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-900">
                      <strong>¡Bienvenido/a de nuevo!</strong> Completamos tus datos con la información de tu última consulta. Verificá que estén correctos antes de continuar.
                    </p>
                  </div>
                )}

                {/* Consult intro + cost transparency. The disclaimer sits on
                    step 1 so patients see the "may have a cost" advisory
                    BEFORE investing time filling personal data — previous
                    flow surprised them at step 2 (right after choosing a
                    category) which caused abandonment. Copy is generalized
                    because recetas + órdenes de estudios are free today. */}
                <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-md" data-testid="consult-intro-notice">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900 leading-relaxed">
                      <strong>Estás solicitando una consulta.</strong>
                      <br />
                      Según el tipo de consulta puede aplicarse un costo.
                    </p>
                  </div>
                </div>

                {/* Third-party toggle (Ley 25.326 / Ley 26.529) */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                  <Label className="text-sm font-medium text-slate-800">
                    ¿La consulta es para vos o para otra persona que cuidás? *
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className={`flex items-start p-3 rounded-md border cursor-pointer transition-colors ${!formData.is_third_party ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white hover:bg-slate-100'}`}>
                      <input
                        type="radio"
                        name="consult_for"
                        data-testid="consult-for-self"
                        checked={!formData.is_third_party}
                        onChange={() => setFormData({
                          ...formData,
                          is_third_party: false,
                          submitter_name: "",
                          submitter_email: "",
                          submitter_dni: "",
                          submitter_relationship: "",
                          submitter_relationship_other: "",
                          patient_authorization_declared: false,
                        })}
                        className="mt-0.5 mr-2"
                      />
                      <span className="text-sm text-slate-700">Para mí (soy el paciente)</span>
                    </label>
                    <label className={`flex items-start p-3 rounded-md border cursor-pointer transition-colors ${formData.is_third_party ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white hover:bg-slate-100'}`}>
                      <input
                        type="radio"
                        name="consult_for"
                        data-testid="consult-for-third-party"
                        checked={formData.is_third_party}
                        onChange={() => setFormData({ ...formData, is_third_party: true })}
                        className="mt-0.5 mr-2"
                      />
                      <span className="text-sm text-slate-700">Para otra persona que cuido</span>
                    </label>
                  </div>

                  {formData.is_third_party && (
                    <div className="pt-2 space-y-3" data-testid="third-party-fields">
                      <p className="text-xs text-slate-600 italic">
                        Completá tus datos como persona que realiza la consulta. Después te vamos a pedir los datos del paciente.
                      </p>

                      <div>
                        <Label htmlFor="submitter_name" className="text-sm">Tu nombre completo *</Label>
                        <Input
                          id="submitter_name"
                          data-testid="submitter-name-input"
                          value={formData.submitter_name}
                          onChange={(e) => setFormData({ ...formData, submitter_name: e.target.value })}
                          placeholder="Ej: María García"
                          required={formData.is_third_party}
                          className="h-11 mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="submitter_dni" className="text-sm">Tu DNI *</Label>
                          <Input
                            id="submitter_dni"
                            data-testid="submitter-dni-input"
                            value={formData.submitter_dni}
                            onChange={(e) => setFormData({ ...formData, submitter_dni: e.target.value.replace(/\D/g, "") })}
                            placeholder="Sin puntos"
                            required={formData.is_third_party}
                            inputMode="numeric"
                            className="h-11 mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="submitter_email" className="text-sm">Tu email *</Label>
                          <Input
                            id="submitter_email"
                            type="email"
                            data-testid="submitter-email-input"
                            value={formData.submitter_email}
                            onChange={(e) => setFormData({ ...formData, submitter_email: e.target.value })}
                            placeholder="tu@email.com"
                            required={formData.is_third_party}
                            className="h-11 mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="submitter_relationship" className="text-sm">Relación con el paciente *</Label>
                        <Select
                          value={formData.submitter_relationship}
                          onValueChange={(value) => setFormData({ ...formData, submitter_relationship: value, submitter_relationship_other: "" })}
                        >
                          <SelectTrigger className="h-11 mt-1" data-testid="submitter-relationship-trigger">
                            <SelectValue placeholder="Seleccioná..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spouse">Cónyuge / pareja</SelectItem>
                            <SelectItem value="parent">Padre / madre del paciente</SelectItem>
                            <SelectItem value="child">Hijo/a del paciente</SelectItem>
                            <SelectItem value="sibling">Hermano/a del paciente</SelectItem>
                            <SelectItem value="caregiver_formal">Cuidador/a formal (con vínculo profesional)</SelectItem>
                            <SelectItem value="other">Otra</SelectItem>
                          </SelectContent>
                        </Select>
                        {formData.submitter_relationship === "other" && (
                          <Input
                            data-testid="submitter-relationship-other-input"
                            value={formData.submitter_relationship_other}
                            onChange={(e) => setFormData({ ...formData, submitter_relationship_other: e.target.value })}
                            placeholder="Especificá tu relación con el paciente"
                            className="h-11 mt-2"
                            maxLength={80}
                            required
                          />
                        )}
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
                        <p className="text-xs text-amber-900 leading-relaxed">
                          <strong>Declaración de autorización (obligatoria).</strong>
                          {" "}
                          Por motivos legales (Ley 25.326 de Habeas Data y Ley 26.529 de Derechos del Paciente), necesitamos que confirmes lo siguiente:
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer text-xs text-amber-900">
                          <input
                            type="checkbox"
                            data-testid="patient-authorization-checkbox"
                            checked={formData.patient_authorization_declared}
                            onChange={(e) => setFormData({ ...formData, patient_authorization_declared: e.target.checked })}
                            className="mt-0.5 h-4 w-4 flex-shrink-0"
                          />
                          <span className="leading-relaxed">
                            Declaro bajo juramento que el paciente me autorizó expresamente a realizar esta consulta médica en su nombre y a recibir información sobre su salud. Asumo la responsabilidad legal de esta declaración.
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Patient data section header (when third-party) */}
                {formData.is_third_party && (
                  <div className="pt-2 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800">Datos del paciente</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Completá los datos de la persona para quien hacés la consulta.
                    </p>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="patient_name">{formData.is_third_party ? "Nombre completo del paciente *" : "Nombre completo *"}</Label>
                  <Input
                    id="patient_name"
                    data-testid="patient-name-input"
                    value={formData.patient_name}
                    onChange={(e) => setFormData({...formData, patient_name: e.target.value})}
                    placeholder="Ej: Juan Pérez"
                    required
                    className="h-12 mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="patient_dni">DNI o ID del paciente *</Label>
                  <Input
                    id="patient_dni"
                    data-testid="patient-dni-input"
                    value={formData.patient_dni}
                    onChange={(e) => setFormData({...formData, patient_dni: e.target.value})}
                    placeholder="Ej: 30123456"
                    required
                    className="h-12 mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="patient_email">{formData.is_third_party ? "Email del paciente *" : "Email *"}</Label>
                  <Input
                    id="patient_email"
                    type="email"
                    data-testid="patient-email-input"
                    value={formData.patient_email}
                    onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
                    placeholder="juan@ejemplo.com"
                    required
                    autoComplete="email"
                    className="h-12 mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.is_third_party
                      ? "La respuesta del médico será enviada a este email del paciente. Tu email queda registrado para auditoría."
                      : "Recibirás la respuesta en este email"}
                  </p>
                  {/* Typo suggestion: clickable to autofill the corrected email */}
                  {formData.patient_email && (() => {
                    const suggestion = suggestEmailCorrection(formData.patient_email);
                    if (!suggestion) return null;
                    return (
                      <button
                        type="button"
                        data-testid="email-suggestion-btn"
                        onClick={() => setFormData({
                          ...formData,
                          patient_email: suggestion,
                          // If confirm field is empty or matches the suggestion, sync it
                          patient_email_confirm: formData.patient_email_confirm
                            ? formData.patient_email_confirm
                            : "",
                        })}
                        className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 w-full text-left hover:bg-amber-100 transition-colors flex items-start gap-2"
                      >
                        <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>
                          ¿Quisiste decir <strong>{suggestion}</strong>? Hacé click para corregir.
                        </span>
                      </button>
                    );
                  })()}
                  {/* Format error: only when user has typed something with @ */}
                  {formData.patient_email && formData.patient_email.includes("@") && !isValidEmailFormat(formData.patient_email) && (
                    <p className="text-xs text-red-600 mt-1" data-testid="email-format-error">
                      Email inválido. Verificá que tenga el formato <em>nombre@dominio.com</em>.
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="patient_email_confirm">Confirmar email *</Label>
                  <Input
                    id="patient_email_confirm"
                    type="email"
                    data-testid="patient-email-confirm-input"
                    value={formData.patient_email_confirm}
                    onChange={(e) => setFormData({...formData, patient_email_confirm: e.target.value})}
                    onPaste={(e) => {
                      // Prevent paste so users have to retype (catches copy-paste of typo)
                      e.preventDefault();
                      toast.error("Por seguridad, escribí el email manualmente para confirmarlo");
                    }}
                    placeholder="Repetí tu email"
                    required
                    autoComplete="off"
                    className="h-12 mt-1"
                  />
                  {formData.patient_email_confirm &&
                    formData.patient_email.trim().toLowerCase() !== formData.patient_email_confirm.trim().toLowerCase() && (
                      <p className="text-xs text-red-600 mt-1" data-testid="email-mismatch-error">
                        Los emails no coinciden.
                      </p>
                    )}
                  {formData.patient_email_confirm &&
                    formData.patient_email.trim().toLowerCase() === formData.patient_email_confirm.trim().toLowerCase() &&
                    isValidEmailFormat(formData.patient_email) && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="email-match-ok">
                        <CheckCircle2 className="h-3 w-3" />
                        Email confirmado
                      </p>
                    )}
                </div>

                <div>
                  <Label htmlFor="patient_phone">Teléfono (opcional)</Label>
                  <Input
                    id="patient_phone"
                    type="tel"
                    data-testid="patient-phone-input"
                    value={formData.patient_phone}
                    onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                    placeholder="Ej: +54 11 1234-5678"
                    className="h-12 mt-1"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="obra_social">Obra Social / Prepaga <span className="text-slate-400 font-normal text-xs">(opcional)</span></Label>
                    <Input
                      id="obra_social"
                      data-testid="obra-social-input"
                      value={formData.obra_social}
                      onChange={(e) => setFormData({...formData, obra_social: e.target.value})}
                      placeholder="Ej: OSDE, Swiss Medical. Dejar en blanco si no tenés."
                      className="h-12 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="credencial_number">N° de Credencial <span className="text-slate-400 font-normal text-xs">(opcional)</span></Label>
                    <Input
                      id="credencial_number"
                      data-testid="credencial-number-input"
                      value={formData.credencial_number}
                      onChange={(e) => setFormData({...formData, credencial_number: e.target.value})}
                      placeholder="Ej: 84523167209"
                      className="h-12 mt-1"
                    />
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={
                    !formData.patient_name ||
                    !formData.patient_email ||
                    !formData.patient_email_confirm ||
                    !isValidEmailFormat(formData.patient_email) ||
                    formData.patient_email.trim().toLowerCase() !== formData.patient_email_confirm.trim().toLowerCase() ||
                    !formData.patient_dni ||
                    (formData.is_third_party && (
                      !formData.submitter_name ||
                      !formData.submitter_email ||
                      !isValidEmailFormat(formData.submitter_email) ||
                      !formData.submitter_dni ||
                      !formData.submitter_relationship ||
                      (formData.submitter_relationship === "other" && !formData.submitter_relationship_other) ||
                      !formData.patient_authorization_declared
                    ))
                  }
                  className="w-full h-12 text-white"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="next-step-1-btn"
                >
                  Continuar con mi consulta
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    if (onCancel) {
                      // Use callback to go back (for embedded forms)
                      onCancel();
                    } else if (doctor && sourceSlug) {
                      // Go back to doctor landing page
                      navigate(`/${sourceSlug}`);
                    } else {
                      // Go to main landing page
                      navigate('/');
                    }
                  }}
                  className="w-full h-10 text-slate-500 hover:text-slate-700"
                  data-testid="cancel-step-1-btn"
                >
                  Cancelar
                </Button>
              </div>
            )}

            {/* Step 2: Consult Type */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900">Tipo de Consulta</h2>
                </div>

                <div>
                  <Label>Tipo de consulta *</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Tocá una opción. Si necesitás más detalle, tocá el ícono <Info className="inline h-3.5 w-3.5 -mt-0.5" /> al lado.
                  </p>
                  <TooltipProvider delayDuration={100}>
                    <div className="flex flex-col gap-2" data-testid="category-cards-step2" role="radiogroup" aria-label="Tipo de consulta">
                      {CATEGORY_CARDS.map((card) => {
                        const selected = formData.category === card.id;
                        return (
                          <div
                            key={card.id}
                            className={`flex items-stretch rounded-xl border transition-all ${
                              selected
                                ? "border-primary bg-primary/5 shadow-sm"
                                : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <button
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              onClick={() => setFormData({ ...formData, category: card.id })}
                              data-testid={`category-card-${card.id}`}
                              className={`flex-1 text-left px-4 py-4 rounded-l-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                                selected ? "text-primary font-semibold" : "text-slate-800 font-medium"
                              }`}
                            >
                              {card.label}
                            </button>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label={`Info sobre ${card.label}`}
                                  data-testid={`category-info-${card.id}`}
                                  className="px-3 flex items-center justify-center text-slate-400 hover:text-primary focus:outline-none focus-visible:text-primary rounded-r-xl"
                                >
                                  <Info className="h-5 w-5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="left"
                                className="max-w-[260px] bg-slate-800 text-white text-sm p-3"
                              >
                                <p>{PATIENT_CATEGORY_TOOLTIPS[card.id]}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                </div>

                {/* Cost transparency is handled up-front on step 1's blue
                    notice, so we don't repeat it here. The receta variant
                    below is kept because it carries category-specific info
                    (courier flow), not a cost warning. */}
                {formData.category === 'recetas' && (
                  <div
                    className="p-3 bg-slate-50 border border-slate-200 rounded-md"
                    data-testid="category-disclaimer-recetas"
                  >
                    <p className="text-sm text-slate-600 leading-relaxed">
                      <strong>Este canal no reemplaza autorizaciones ni cobertura de tu Obra Social.</strong>{" "}
                      Es un servicio asincrónico complementario. Si tu medicación requiere autorización específica, tu médico te lo indicará.
                    </p>
                  </div>
                )}

                {/* Dynamic form based on category */}
                {formData.category === 'resultados' && (
                  <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-semibold text-slate-900">Interpretación de resultados</h3>
                    <div>
                      <Label htmlFor="results_comment">Descripción *</Label>
                      <Textarea
                        id="results_comment"
                        data-testid="results-comment-textarea"
                        value={formData.specific_fields.results_comment}
                        onChange={(e) => {
                          if (e.target.value.length <= 800) {
                            setFormData({
                              ...formData,
                              specific_fields: { ...formData.specific_fields, results_comment: e.target.value },
                              consult_reason: e.target.value
                            });
                          }
                        }}
                        placeholder="Indique qué resultado desea interpretar, y una breve descripción de los síntomas o motivo de solicitud del estudio. Si tiene un enlace al estudio online, incluya el link, usuario y contraseña si corresponde."
                        rows={6}
                        required
                        maxLength={800}
                        className="resize-none mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-right">
                        {formData.specific_fields.results_comment.length}/800 caracteres
                      </p>
                    </div>
                    
                    {/* File Upload Section */}
                    <div className="space-y-3">
                      <Label>Adjuntar archivos (opcional)</Label>
                      <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-white">
                        <input
                          type="file"
                          id="file-upload"
                          data-testid="file-upload-input"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        <label htmlFor="file-upload" className="flex flex-col items-center cursor-pointer">
                          <Upload className={`h-8 w-8 ${uploading ? 'text-slate-300' : 'text-blue-500'}`} />
                          <span className="mt-2 text-sm text-slate-600">
                            {uploading ? 'Subiendo...' : 'Haz clic o arrastra archivos aquí'}
                          </span>
                          <span className="text-xs text-slate-400 mt-1">
                            PDF, imágenes, Word, Excel (máx. 10MB c/u, hasta 5 archivos)
                          </span>
                        </label>
                      </div>
                      
                      {uploadedFiles.length > 0 && (
                        <div className="space-y-2">
                          {uploadedFiles.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                              <div className="flex items-center space-x-2">
                                {file.type.includes('image') ? (
                                  <Image className="h-5 w-5 text-green-500" />
                                ) : (
                                  <FileText className="h-5 w-5 text-blue-500" />
                                )}
                                <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                                <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                              </div>
                              <button type="button" onClick={() => removeFile(file.id)} className="p-1 hover:bg-red-50 rounded">
                                <X className="h-4 w-4 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {formData.category === 'seguimiento' && (
                  <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h3 className="font-semibold text-slate-900">Seguimiento / control</h3>
                    <div>
                      <Label htmlFor="followup_description">Descripción *</Label>
                      <Textarea
                        id="followup_description"
                        data-testid="followup-description-textarea"
                        value={formData.specific_fields.followup_description}
                        onChange={(e) => {
                          if (e.target.value.length <= 1000) {
                            setFormData({
                              ...formData,
                              specific_fields: { ...formData.specific_fields, followup_description: e.target.value },
                              consult_reason: e.target.value
                            });
                          }
                        }}
                        placeholder="Describa cómo se ha sentido desde la última consulta, si hubo cambios en los síntomas o en el tratamiento."
                        rows={6}
                        required
                        maxLength={1000}
                        className="resize-none mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-right">
                        {formData.specific_fields.followup_description.length}/1000 caracteres
                      </p>
                    </div>
                  </div>
                )}

                {formData.category === 'medicacion' && (
                  <div className="space-y-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h3 className="font-semibold text-slate-900">Tratamiento / efectos adversos</h3>
                    
                    {/* Subtipo de consulta */}
                    <div>
                      <Label>¿Qué tipo de consulta sobre tu tratamiento necesitás? *</Label>
                      <div className="mt-2 space-y-2">
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
                          <input
                            type="radio"
                            name="medication_subtipo"
                            value="consulta_general"
                            checked={formData.specific_fields.medication_subtipo === 'consulta_general'}
                            onChange={(e) => setFormData({
                              ...formData,
                              specific_fields: { 
                                ...formData.specific_fields, 
                                medication_subtipo: e.target.value,
                                inicio_sintomas: "",
                                intensidad_alta: null,
                                adverse_red_flags: []
                              }
                            })}
                            className="mr-3"
                          />
                          <div>
                            <span className="font-medium text-slate-900">Consulta general sobre tratamiento</span>
                            <p className="text-sm text-slate-500">Dudas sobre dosis, interacciones, cambios de tratamiento</p>
                          </div>
                        </label>
                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
                          <input
                            type="radio"
                            name="medication_subtipo"
                            value="efecto_adverso"
                            checked={formData.specific_fields.medication_subtipo === 'efecto_adverso'}
                            onChange={(e) => setFormData({
                              ...formData,
                              specific_fields: { 
                                ...formData.specific_fields, 
                                medication_subtipo: e.target.value 
                              }
                            })}
                            className="mr-3"
                          />
                          <div>
                            <span className="font-medium text-slate-900">Posible efecto adverso</span>
                            <p className="text-sm text-slate-500">Síntomas o molestias que aparecieron tras iniciar el tratamiento</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Formulario para consulta general */}
                    {formData.specific_fields.medication_subtipo === 'consulta_general' && (
                      <div>
                        <Label htmlFor="medication_concern">Descripción *</Label>
                        <Textarea
                          id="medication_concern"
                          data-testid="medication-concern-textarea"
                          value={formData.specific_fields.medication_concern}
                          onChange={(e) => {
                            if (e.target.value.length <= 1000) {
                              setFormData({
                                ...formData,
                                specific_fields: { ...formData.specific_fields, medication_concern: e.target.value },
                                consult_reason: e.target.value
                              });
                            }
                          }}
                          placeholder="Describa su consulta sobre el tratamiento: dudas sobre dosis, interacciones, cambios, etc."
                          rows={6}
                          required
                          maxLength={1000}
                          className="resize-none mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1 text-right">
                          {formData.specific_fields.medication_concern.length}/1000 caracteres
                        </p>
                      </div>
                    )}

                    {/* Formulario de triaje para efecto adverso */}
                    {formData.specific_fields.medication_subtipo === 'efecto_adverso' && (
                      <div className="space-y-5">
                        {/* Descripción del efecto */}
                        <div>
                          <Label htmlFor="medication_concern">Describí los síntomas o molestias *</Label>
                          <Textarea
                            id="medication_concern"
                            data-testid="medication-concern-textarea"
                            value={formData.specific_fields.medication_concern}
                            onChange={(e) => {
                              if (e.target.value.length <= 1000) {
                                setFormData({
                                  ...formData,
                                  specific_fields: { ...formData.specific_fields, medication_concern: e.target.value },
                                  consult_reason: e.target.value
                                });
                              }
                            }}
                            placeholder="Describa los síntomas o molestias que nota con este tratamiento."
                            rows={4}
                            required
                            maxLength={1000}
                            className="resize-none mt-1"
                          />
                          <p className="text-xs text-slate-500 mt-1 text-right">
                            {formData.specific_fields.medication_concern.length}/1000 caracteres
                          </p>
                        </div>

                        {/* Pregunta 1: Tiempo de inicio */}
                        <div className="p-4 bg-white border border-purple-200 rounded-lg">
                          <Label className="text-base">¿Cuándo comenzaron los síntomas? *</Label>
                          <div className="mt-3 space-y-2">
                            {[
                              { value: 'menos_24h', label: 'Menos de 24 horas' },
                              { value: '1_2_dias', label: '1 a 2 días' },
                              { value: 'mas_2_dias', label: 'Más de 2 días' }
                            ].map((option) => (
                              <label key={option.value} className="flex items-center p-2 rounded hover:bg-slate-50 cursor-pointer">
                                <input
                                  type="radio"
                                  name="inicio_sintomas"
                                  data-testid={`inicio-sintomas-${option.value}`}
                                  value={option.value}
                                  checked={formData.specific_fields.inicio_sintomas === option.value}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    specific_fields: { ...formData.specific_fields, inicio_sintomas: e.target.value }
                                  })}
                                  className="mr-3"
                                  required
                                />
                                <span className="text-slate-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Pregunta 2: Severidad subjetiva */}
                        <div className="p-4 bg-white border border-purple-200 rounded-lg">
                          <Label className="text-base">¿Cómo describís la intensidad del síntoma? *</Label>
                          <div className="mt-3 space-y-2">
                            {[
                              { value: 'leve', label: 'Leve', description: 'Molesto pero tolerable' },
                              { value: 'moderado', label: 'Moderado', description: 'Interfiere con actividades' },
                              { value: 'intenso', label: 'Intenso', description: 'Impide actividades habituales' }
                            ].map((option) => (
                              <label key={option.value} className="flex items-start p-3 rounded hover:bg-slate-50 cursor-pointer border border-transparent hover:border-purple-200">
                                <input
                                  type="radio"
                                  name="severidad"
                                  data-testid={`severidad-${option.value}`}
                                  value={option.value}
                                  checked={formData.specific_fields.severidad === option.value}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    specific_fields: { ...formData.specific_fields, severidad: e.target.value }
                                  })}
                                  className="mr-3 mt-0.5"
                                  required
                                />
                                <div>
                                  <span className="text-slate-900 font-medium">{option.label}</span>
                                  <p className="text-sm text-slate-500">{option.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Pregunta 3: Red Flags - Criterios de exclusión */}
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-2 mb-3">
                            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                            <div>
                              <Label className="text-base text-red-900">¿Presenta alguno de estos síntomas de alarma?</Label>
                              <p className="text-sm text-red-700 mt-1">Marque todos los que apliquen. Si marca alguno, su consulta será derivada a atención presencial urgente.</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-2">
                            {[
                              { value: 'dificultad_respirar', label: 'Dificultad para respirar' },
                              { value: 'hinchazon_cara', label: 'Hinchazón de cara, labios o lengua' },
                              { value: 'dolor_pecho', label: 'Dolor en el pecho' },
                              { value: 'perdida_conciencia', label: 'Pérdida de conocimiento o desmayo' },
                              { value: 'convulsion', label: 'Convulsión' },
                              { value: 'debilidad_subita', label: 'Debilidad súbita de un brazo o pierna' },
                              { value: 'confusion', label: 'Confusión o alteración del estado de conciencia' },
                              { value: 'sangrado_vomitos', label: 'Sangrado importante o vómitos con sangre' },
                              { value: 'erupcion_fiebre', label: 'Erupción cutánea extensa con fiebre o ampollas' },
                              { value: 'dolor_abdominal', label: 'Dolor abdominal intenso y persistente' },
                              { value: 'perdida_vision', label: 'Pérdida repentina de visión' }
                            ].map((flag) => (
                              <label key={flag.value} className="flex items-center p-2 bg-white rounded border hover:bg-red-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  data-testid={`red-flag-${flag.value}`}
                                  checked={formData.specific_fields.adverse_red_flags.includes(flag.value)}
                                  onChange={(e) => {
                                    const newFlags = e.target.checked
                                      ? [...formData.specific_fields.adverse_red_flags, flag.value]
                                      : formData.specific_fields.adverse_red_flags.filter(f => f !== flag.value);
                                    setFormData({
                                      ...formData,
                                      specific_fields: { ...formData.specific_fields, adverse_red_flags: newFlags }
                                    });
                                  }}
                                  className="mr-3 h-4 w-4"
                                />
                                <span className="text-sm text-slate-700">{flag.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {formData.category === 'recetas' && (
                  <div className="space-y-4 p-4 bg-purple-50 border border-purple-200 rounded-lg" data-testid="recetas-form">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h3 className="font-semibold text-slate-900">Solicitud de recetas</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="repeat-previous-recetas-btn"
                        onClick={async () => {
                          if (!formData.patient_dni || !formData.patient_email) {
                            toast.error("Completá tu DNI y email más abajo para ver tus recetas anteriores");
                            return;
                          }
                          try {
                            const params = new URLSearchParams({
                              dni: formData.patient_dni,
                              email: formData.patient_email,
                            });
                            const res = await fetch(`${API_URL}/api/intake/previous-recetas?${params.toString()}`);
                            if (res.ok) {
                              const data = await res.json();
                              setPreviousRecetas(Array.isArray(data) ? data : []);
                              if (!data || data.length === 0) {
                                toast.info("No encontramos recetas previas con este DNI");
                              } else {
                                setShowPreviousRecetasModal(true);
                              }
                            }
                          } catch (e) {
                            console.error('Error loading previous recetas:', e);
                            toast.error("No pudimos cargar tus recetas anteriores");
                          }
                        }}
                      >
                        Repetir solicitud anterior
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600">
                      Renovación de medicación que ya tomás, sin cambios. Sin costo en Fleni.
                      Si necesitás ajustar dosis o reportar efectos adversos, usá la categoría <strong>Consulta sobre tu tratamiento</strong>.
                    </p>

                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-3" data-testid="receta-disclaimer-box">
                      <div className="flex gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-900 leading-relaxed">
                          Este canal es solo para <strong>renovaciones simples</strong>.
                          Si tu medicación requiere autorización de obra social, resumen de historia clínica o documentación complementaria, tu médico no podrá emitirla por este medio.
                          En esos casos, seleccioná la opción <strong>&quot;Documentación / Resumen de historia clínica&quot;</strong> en el menú de tipo de consulta.
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="medications_text">Medicación a renovar *</Label>
                      <p className="text-xs text-slate-600 mb-2 mt-1">
                        Podés pedir <strong>todos los medicamentos que necesites en un mismo pedido</strong>. Un pedido = una sola consulta.
                      </p>
                      <Textarea
                        id="medications_text"
                        data-testid="medications-textarea"
                        rows={6}
                        placeholder="Ej: Levetiracetam 500 mg comp. x 60. Una caja. (Opcional: marca)"
                        value={formData.specific_fields.medications_text}
                        onChange={(e) => setFormData({
                          ...formData,
                          // Keep consult_reason in sync so the generic "Continuar" button (which validates
                          // consult_reason) enables once the patient typed at least one medication.
                          consult_reason: e.target.value,
                          specific_fields: { ...formData.specific_fields, medications_text: e.target.value }
                        })}
                        className="mt-1"
                      />
                      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                        <p className="text-xs text-slate-500">
                          Incluí droga, dosis (mg), forma farmacéutica, número de envases y marca si la necesitás.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="add-medication-btn"
                          onClick={() => {
                            const current = formData.specific_fields.medications_text || '';
                            const trimmedEnd = current.replace(/\s+$/, '');
                            // Count existing numbered lines to continue the sequence.
                            const lines = trimmedEnd ? trimmedEnd.split('\n') : [];
                            const numberedCount = lines.filter(l => /^\s*\d+[\.\)]\s*/.test(l)).length;
                            let nextNumber;
                            let prefix;
                            if (!trimmedEnd) {
                              // Empty textarea: start the numbered list at 1.
                              nextNumber = 1;
                              prefix = '';
                            } else if (numberedCount === 0) {
                              // Existing free text without numbering: convert into item 1 and append item 2.
                              const asItem1 = `1. ${trimmedEnd}`;
                              const newValue = `${asItem1}\n2. `;
                              setFormData({
                                ...formData,
                                consult_reason: newValue,
                                specific_fields: { ...formData.specific_fields, medications_text: newValue }
                              });
                              setTimeout(() => {
                                const ta = document.getElementById('medications_text');
                                if (ta) { ta.focus(); ta.setSelectionRange(newValue.length, newValue.length); }
                              }, 0);
                              return;
                            } else {
                              nextNumber = numberedCount + 1;
                              prefix = '\n';
                            }
                            const newValue = `${trimmedEnd}${prefix}${nextNumber}. `;
                            setFormData({
                              ...formData,
                              consult_reason: newValue,
                              specific_fields: { ...formData.specific_fields, medications_text: newValue }
                            });
                            setTimeout(() => {
                              const ta = document.getElementById('medications_text');
                              if (ta) { ta.focus(); ta.setSelectionRange(newValue.length, newValue.length); }
                            }, 0);
                          }}
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          Agregar otro medicamento
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {formData.category === 'documentacion' && (
                  <div className="space-y-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h3 className="font-semibold text-slate-900">Documentación (certificado / orden / informe)</h3>
                    <div>
                      <Label htmlFor="document_reason">Descripción *</Label>
                      <Textarea
                        id="document_reason"
                        data-testid="document-reason-textarea"
                        value={formData.specific_fields.document_reason}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setFormData({
                              ...formData,
                              specific_fields: { ...formData.specific_fields, document_reason: e.target.value },
                              consult_reason: e.target.value
                            });
                          }
                        }}
                        placeholder="Indique qué documentación necesita (resumen de historia clínica, certificado médico, orden de estudios, etc.). En caso de que deba ser completada en formularios específicos de su obra social o prepaga, por favor haga esta aclaración y adjúntelos abajo."
                        rows={5}
                        required
                        maxLength={500}
                        className="resize-none mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-right">
                        {formData.specific_fields.document_reason.length}/500 caracteres
                      </p>
                    </div>
                    
                    {/* File Upload Section for Documentation */}
                    <div className="space-y-3">
                      <Label>Adjuntar archivos (opcional)</Label>
                      <p className="text-xs text-slate-600 -mt-1">
                        Si su obra social o prepaga requiere formularios específicos, adjúntelos aquí.
                      </p>
                      <div className="border-2 border-dashed border-orange-300 rounded-lg p-4 bg-white">
                        <input
                          type="file"
                          id="doc-file-upload"
                          data-testid="doc-file-upload-input"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        <label htmlFor="doc-file-upload" className="flex flex-col items-center cursor-pointer">
                          <Upload className={`h-8 w-8 ${uploading ? 'text-slate-300' : 'text-orange-500'}`} />
                          <span className="mt-2 text-sm text-slate-600">
                            {uploading ? 'Subiendo...' : 'Haz clic o arrastra archivos aquí'}
                          </span>
                          <span className="text-xs text-slate-400 mt-1">
                            PDF, imágenes, Word, Excel (máx. 10MB c/u, hasta 5 archivos)
                          </span>
                        </label>
                      </div>
                      
                      {uploadedFiles.length > 0 && (
                        <div className="space-y-2">
                          {uploadedFiles.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-white border rounded-lg">
                              <div className="flex items-center space-x-2">
                                {file.type.includes('image') ? (
                                  <Image className="h-5 w-5 text-green-500" />
                                ) : (
                                  <FileText className="h-5 w-5 text-orange-500" />
                                )}
                                <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                                <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                              </div>
                              <button type="button" onClick={() => removeFile(file.id)} className="p-1 hover:bg-red-50 rounded">
                                <X className="h-4 w-4 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {formData.category === 'otra' && (
                  <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h3 className="font-semibold text-slate-900">No estoy seguro / otra consulta</h3>
                    <div>
                      <Label htmlFor="other_description">Descripción *</Label>
                      <Textarea
                        id="other_description"
                        data-testid="other-description-textarea"
                        value={formData.specific_fields.other_description}
                        onChange={(e) => {
                          if (e.target.value.length <= 800) {
                            setFormData({
                              ...formData,
                              specific_fields: { ...formData.specific_fields, other_description: e.target.value },
                              consult_reason: e.target.value
                            });
                          }
                        }}
                        placeholder="Describa brevemente su consulta o inquietud. Puede que este tipo de consulta sea derivada a una Teleconsulta o consulta presencial. En tal caso, no se le aplicarán cargos."
                        rows={6}
                        required
                        maxLength={800}
                        className="resize-none mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-right">
                        {formData.specific_fields.other_description.length}/800 caracteres
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12"
                  >
                    Atrás
                  </Button>
                  {/* Recetas skip step 3 entirely: Thales is a courier for the
                      request, the actual prescription is issued by the doctor
                      in Fleni's official system (SIDCA / HC Digital). No
                      clinical consent or red-flag triage applies — we jump
                      straight to the confirmation modal that already lists the
                      requested meds. */}
                  {formData.category === 'recetas' ? (
                    <Button
                      type="button"
                      onClick={() => {
                        const meds = (formData.specific_fields.medications_text || '').trim();
                        if (!meds) {
                          toast.error("Ingresá al menos un medicamento");
                          return;
                        }
                        setShowRecetaConfirm(true);
                      }}
                      disabled={!formData.category || !formData.consult_reason}
                      className="flex-1 h-12 text-white"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="receta-solicit-btn"
                    >
                      Solicitar receta
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setStep(3)}
                      disabled={!formData.category || !formData.consult_reason}
                      className="flex-1 h-12 text-white"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="next-step-2-btn"
                    >
                      Continuar
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900">Confirmación</h2>
                  <p className="text-slate-500 text-sm mt-1">Revise la información antes de enviar</p>
                </div>

                {/* Red Flags Warning — skipped for receta (prescription renewal) requests */}
                {formData.category !== 'recetas' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-900 mb-2">
                        ¿Presenta alguno de estos síntomas de alarma?
                      </p>
                      <p className="text-sm text-red-700 mb-3">
                        Marque todos los que apliquen. Si marca alguno, su consulta será derivada a atención presencial urgente.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {RED_FLAGS.map((flag) => (
                          <label key={flag.key} className="flex items-center p-2 bg-white rounded border hover:bg-red-50 cursor-pointer">
                            <input
                              type="checkbox"
                              id={flag.key}
                              data-testid={`red-flag-${flag.key}`}
                              checked={formData.red_flags[flag.key] || pastRedFlags.some(p => p.key === flag.key)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (!checked) {
                                  // Unchecking: clear state and any past-flag record for this key
                                  setFormData({
                                    ...formData,
                                    red_flags: { ...formData.red_flags, [flag.key]: false }
                                  });
                                  setPastRedFlags(prev => prev.filter(p => p.key !== flag.key));
                                  return;
                                }
                                // Checking: open confirmation dialog. Do NOT update state yet.
                                // The dialog will decide what to do based on patient's answer.
                                setRedFlagPendingConfirm({ key: flag.key, label: flag.label });
                              }}
                              className="mr-3 h-4 w-4"
                            />
                            <span className="text-sm text-red-800">{flag.label}</span>
                          </label>
                        ))}
                      </div>

                      {/* Past red flags: registered as historical, will be flagged to doctor */}
                      {pastRedFlags.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="past-red-flags-block">
                          <p className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Síntomas recientes registrados (ya pasaron)
                          </p>
                          <p className="text-xs text-amber-800 mb-2">
                            Estos síntomas serán informados a tu médico junto con tu consulta para que les preste atención particular.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {pastRedFlags.map(pf => (
                              <span
                                key={pf.key}
                                data-testid={`past-flag-badge-${pf.key}`}
                                className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-300 text-amber-900 rounded-full px-3 py-1"
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                                {pf.label}
                                <button
                                  type="button"
                                  onClick={() => setPastRedFlags(prev => prev.filter(p => p.key !== pf.key))}
                                  className="ml-1 text-amber-600 hover:text-amber-900"
                                  aria-label={`Quitar ${pf.label}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )}

                {/* Summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                  <h3 className="font-semibold text-slate-900">Resumen de la consulta</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Nombre:</span>
                      <p className="font-medium">{formData.patient_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">DNI:</span>
                      <p className="font-medium">{formData.patient_dni}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <p className="font-medium">{formData.patient_email}</p>
                    </div>
                    {formData.patient_phone && (
                      <div>
                        <span className="text-slate-500">Teléfono:</span>
                        <p className="font-medium">{formData.patient_phone}</p>
                      </div>
                    )}
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-500">Tipo de consulta:</span>
                    <p className="font-medium capitalize">{formData.category?.replace('_', ' ')}</p>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-500">Descripción:</span>
                    <p className="font-medium text-slate-700 mt-1 whitespace-pre-wrap">
                      {formData.consult_reason?.substring(0, 200)}
                      {formData.consult_reason?.length > 200 && '...'}
                    </p>
                  </div>
                  {uploadedFiles.length > 0 && (
                    <div className="text-sm">
                      <span className="text-slate-500">Archivos adjuntos:</span>
                      <p className="font-medium">{uploadedFiles.length} archivo(s)</p>
                    </div>
                  )}
                </div>

                {/* Legal consent — either the classic explicit checkbox (post-legal-review)
                    or a short informative card (implicit consent, pilot phase). The mode is
                    driven by institution.legal_config.terms_mode. */}
                {(() => {
                  const cfg = institution?.legal_config || {};
                  const mode = cfg.terms_mode || 'explicit';
                  if (mode === 'none') return null;
                  if (mode === 'implicit') {
                    return (
                      <div
                        className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg"
                        data-testid="implicit-consent-card"
                      >
                        <Info className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-800 leading-relaxed">
                          {cfg.implicit_consent_text || (
                            <>
                              Al enviar esta consulta confirmás que entendiste que este es un servicio digital y asincrónico: <strong>no es para urgencias</strong>. Si tenés una emergencia, acudí al servicio de guardia más cercano o llamá al 107. Tus datos se tratan con <strong>confidencialidad médica</strong>.
                            </>
                          )}
                        </p>
                      </div>
                    );
                  }
                  // explicit — the pre-existing checkbox
                  return (
                    <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <Checkbox
                        id="terms"
                        checked={formData.accepted_terms}
                        onCheckedChange={(checked) => setFormData({...formData, accepted_terms: checked})}
                        className="mt-0.5 flex-shrink-0"
                        data-testid="terms-checkbox"
                      />
                      <label htmlFor="terms" className="text-sm text-slate-700 leading-relaxed">
                        Acepto los{" "}
                        <a href={cfg.terms_url || "/terminos"} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">terminos y condiciones</a>
                        {" "}y autorizo el tratamiento de mis datos personales para fines medicos.
                      </label>
                    </div>
                  );
                })()}

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12"
                  >
                    Atrás
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || ((institution?.legal_config?.terms_mode || 'explicit') === 'explicit' && !formData.accepted_terms)}
                    className="flex-1 h-12 text-white"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="submit-consult-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Enviar Consulta
                      </>
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 text-center mt-4">
                  El profesional podrá, bajo su criterio médico, indicar evaluación presencial o teleconsulta sincrónica si lo considera necesario.
                </p>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Red flag confirmation dialog: asks if symptom is current or past */}
      {redFlagPendingConfirm && (
        <div
          role="alertdialog"
          aria-labelledby="red-flag-confirm-heading"
          data-testid="red-flag-confirm-dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <h2
                id="red-flag-confirm-heading"
                className="text-lg sm:text-xl font-bold text-red-900 flex items-center gap-2"
              >
                <AlertTriangle className="h-5 w-5 text-red-700" />
                Antes de continuar
              </h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-700 leading-relaxed">
                Indicaste el síntoma:
              </p>
              <p className="mt-2 mb-4 text-base font-semibold text-slate-900 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                {redFlagPendingConfirm.label}
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                ¿Lo estás teniendo <strong>ahora</strong>, o <strong>ya pasó</strong>?
              </p>
            </div>
            <div className="px-6 pb-6 space-y-2.5">
              <Button
                type="button"
                data-testid="red-flag-confirm-current-btn"
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-base"
                onClick={() => {
                  const flag = redFlagPendingConfirm;
                  setRedFlagPendingConfirm(null);
                  // Mark as active red flag → safety net useEffect will redirect to /urgencia
                  setFormData(prev => ({
                    ...prev,
                    red_flags: { ...prev.red_flags, [flag.key]: true }
                  }));
                  // Audit log
                  fetch(`${API_URL}/api/audit/red-flag-triggered`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      red_flag_key: flag.key,
                      red_flag_label: flag.label,
                      institution_id: institution?.id || null,
                      patient_email: formData.patient_email || null,
                      patient_dni: formData.patient_dni || null
                    })
                  }).catch(() => {});
                }}
              >
                Lo estoy teniendo ahora
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="red-flag-confirm-past-btn"
                className="w-full h-12 text-base border-amber-300 text-amber-900 hover:bg-amber-50"
                onClick={() => {
                  const flag = redFlagPendingConfirm;
                  setRedFlagPendingConfirm(null);
                  // Register as past red flag (does not block submission)
                  setPastRedFlags(prev => {
                    if (prev.some(p => p.key === flag.key)) return prev;
                    return [...prev, { key: flag.key, label: flag.label }];
                  });
                  // Audit log (informational, not urgent)
                  fetch(`${API_URL}/api/audit/red-flag-triggered`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      red_flag_key: flag.key,
                      red_flag_label: flag.label,
                      institution_id: institution?.id || null,
                      patient_email: formData.patient_email || null,
                      patient_dni: formData.patient_dni || null,
                      timing: "past"
                    })
                  }).catch(() => {});
                }}
              >
                Ya pasó, estoy estable ahora
              </Button>
              <p className="text-xs text-slate-500 text-center pt-1">
                Si lo tenés ahora, te derivamos a atención presencial inmediata.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* "Repetir solicitud anterior" modal — listing patient's last receta requests by DNI */}
      {showPreviousRecetasModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="previous-recetas-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Tus recetas anteriores</h3>
              <button
                type="button"
                onClick={() => setShowPreviousRecetasModal(false)}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                aria-label="Cerrar"
              >×</button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              {previousRecetas.length === 0 && (
                <p className="text-sm text-slate-500">No encontramos recetas previas.</p>
              )}
              {previousRecetas.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-lg p-3 hover:border-purple-400 transition" data-testid={`prev-receta-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('es-AR') : '—'}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {r.status === 'Resolved' ? 'Resuelta' : 'Pendiente'}
                    </span>
                  </div>
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap break-words font-sans mb-3 max-h-24 overflow-hidden">
                    {(r.medications_text || r.consult_reason || '').slice(0, 240)}
                    {(r.medications_text || r.consult_reason || '').length > 240 ? '…' : ''}
                  </pre>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid={`use-receta-${r.id}`}
                    onClick={() => {
                      const medsText = r.medications_text || r.consult_reason || '';
                      setFormData(prev => ({
                        ...prev,
                        consult_reason: medsText,
                        specific_fields: {
                          ...prev.specific_fields,
                          medications_text: medsText
                        }
                      }));
                      setShowPreviousRecetasModal(false);
                      toast.success("Solicitud copiada — podés editarla antes de enviar");
                    }}
                  >
                    Usar esta
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recetas confirmation modal — patient reviews the list of medications before submitting.
          Prevents accidental incomplete submits (which would force a new consult for a forgotten med). */}
      {showRecetaConfirm && (
        <div
          role="dialog"
          aria-labelledby="receta-confirm-heading"
          data-testid="receta-confirm-dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <h2 id="receta-confirm-heading" className="text-lg sm:text-xl font-bold text-slate-900">
                Revisá tu pedido antes de enviarlo
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Vas a solicitar los siguientes medicamentos. Si está todo correcto, confirmá.
              </p>
            </div>
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              {/* Brief patient + doctor summary so the modal is a real step of
                  confirmation now that we skip step 3 for recetas. */}
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md space-y-1 text-xs text-slate-700">
                <div><span className="text-slate-500">Paciente:</span> <strong>{formData.patient_name}</strong> · DNI {formData.patient_dni}</div>
                {doctor?.name && (
                  <div><span className="text-slate-500">Enviás a:</span> <strong>{doctor.name}</strong>{institution?.name ? ` · ${institution.name}` : ''}</div>
                )}
              </div>

              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Medicamentos que solicitás
              </p>
              {(() => {
                const raw = (formData.specific_fields.medications_text || '').trim();
                const items = raw
                  .split('\n')
                  .map(l => l.replace(/^\s*\d+[\.\)]\s*/, '').trim())
                  .filter(l => l.length > 0);
                return (
                  <ol
                    className="list-decimal list-inside space-y-2 text-sm text-slate-800"
                    data-testid="receta-confirm-list"
                  >
                    {items.map((item, idx) => (
                      <li key={idx} className="leading-relaxed">{item}</li>
                    ))}
                  </ol>
                );
              })()}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs text-blue-900 leading-relaxed">
                  Si te falta agregar un medicamento, volvé a editar. Una vez enviado, si querés pedir otro tendrás que abrir una nueva solicitud.
                </p>
              </div>

              {/* Courier notice — the actual prescription is issued by the
                  doctor in Fleni's official system, Thales only relays the
                  request. This replaces the formal T&C/red-flag step 3. */}
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="receta-courier-notice">
                <p className="text-xs text-amber-900 leading-relaxed">
                  <strong>Cómo funciona:</strong> esto es solo una solicitud dirigida a tu médico. La receta oficial la emite el profesional en el sistema{institution?.name ? ` de ${institution.name}` : ''} y te llega por su vía habitual. Al enviar, aceptás que compartimos estos datos con el médico para que pueda emitirla.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-11"
                data-testid="receta-confirm-back-btn"
                onClick={() => setShowRecetaConfirm(false)}
                disabled={submitting}
              >
                Volver a editar
              </Button>
              <Button
                type="button"
                className="flex-1 h-11 text-white"
                style={{ backgroundColor: primaryColor }}
                data-testid="receta-confirm-submit-btn"
                disabled={submitting}
                onClick={(e) => {
                  // Re-invoke the same submit flow with the modal-open guard bypassed.
                  // handleSubmit checks !showRecetaConfirm to skip re-opening the modal.
                  setShowRecetaConfirm(false);
                  handleSubmit({ preventDefault: () => {} });
                }}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Confirmar y enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntakeForm;
